/**
 * <s-search-trigger> — Makes the header search field a real, typeable input that
 * hands off to the live predictive search dialog.
 *
 * Behavior:
 *   - Clicking the field opens the dialog (popular searches + trending products show).
 *   - Typing in the field opens the dialog and moves the typed text into the dialog's
 *     input, re-dispatching `input` so live results fetch immediately.
 *   - Without JS (or if the dialog is missing) the wrapped <form> still submits
 *     natively to the search page, so typing + Enter/Search always works.
 *
 * Attributes:
 *   data-target — id of the predictive search <dialog> (e.g. "predictive-search").
 *
 * Required child markup:
 *   input[name="q"] — the visible header field.
 */
class SSearchTrigger extends HTMLElement {
  #input = null;
  #abort = null;

  connectedCallback() {
    this.#input = this.querySelector('input[name="q"]');
    if (!this.#input) return;
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#input.addEventListener('click', () => this.#open(false), { signal });
    this.#input.addEventListener('input', () => this.#open(true), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #open(transferText) {
    const dialog = document.getElementById(this.dataset.target || '');
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const dialogInput = dialog.querySelector('input[name="q"]');

    if (!dialog.open) dialog.showModal();
    if (!dialogInput) return;

    if (transferText) {
      dialogInput.value = this.#input.value;
      this.#input.value = '';
      dialogInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
    dialogInput.focus();
    const end = dialogInput.value.length;
    try {
      dialogInput.setSelectionRange(end, end);
    } catch (_) {
      /* type=search may not support selection in every browser */
    }
  }
}

if (!customElements.get('s-search-trigger')) {
  customElements.define('s-search-trigger', SSearchTrigger);
}
